// The full-screen document view (item 2, C7, 2026-08-13). `main.tsx` mounts it
// instead of the app shell when the URL carries `?doc=<invoiceId>`; see the
// comment there. Same `PdfPane`, same page-span restriction as the modal, fed
// by its own round trip to `getInvoice`/`getUpload`.
import { useEffect, useState } from 'react'
import { Body1, Spinner } from '@fluentui/react-components'
import { PdfPane } from './PdfPane'
import { rotationFromSearch } from './pdfRotation'
import { getInvoice, getUpload } from '../lib/data'
import { loadSession, canPreviewPdf } from '../lib/identity'
import type { Upload } from '../lib/types'
import { errorMessage } from '../api/errors'

type DocState =
  | { status: 'loading' }
  | { status: 'ready'; upload: Upload | undefined; pageStart?: number; pageEnd?: number }
  | { status: 'error'; message: string }
  | { status: 'forbidden' }

/** The `?doc=<invoiceId>` tab: `main.tsx` mounts this instead of the app shell. */
export function StandaloneDocumentView({ invoiceId }: { invoiceId: string }) {
  const [state, setState] = useState<DocState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    // Load this tab's own session first — a `?doc=` tab opened without file
    // access should say so plainly, the same refusal `canPreviewPdf` gives
    // the modal, rather than falling through to `getInvoice`/`getUpload` and
    // rendering a generic "couldn't load" once the API 403s. This is a UX
    // fix, not a security one: the presigned-URL endpoint is still the actual
    // authority and enforces independently of anything checked here.
    loadSession()
      .then(async (session) => {
        if (cancelled) return
        if (!canPreviewPdf(session)) {
          setState({ status: 'forbidden' })
          return
        }
        const invoice = await getInvoice(invoiceId)
        const upload = invoice.upload_id ? await getUpload(invoice.upload_id) : undefined
        if (cancelled) return
        setState({
          status: 'ready',
          upload,
          pageStart: invoice.upload_page_start ?? undefined,
          pageEnd: invoice.upload_page_end ?? undefined,
        })
      })
      .catch((e) => {
        if (!cancelled) setState({ status: 'error', message: errorMessage(e) })
      })
    return () => {
      cancelled = true
    }
  }, [invoiceId])

  if (state.status === 'loading') {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <Spinner label="Loading document…" />
      </div>
    )
  }
  if (state.status === 'forbidden') {
    return (
      <div style={{ padding: 32, maxWidth: 640 }}>
        <Body1>You don't have permission to preview documents. Ask an administrator for document access.</Body1>
      </div>
    )
  }
  if (state.status === 'error') {
    return (
      <div style={{ padding: 32, maxWidth: 640 }}>
        <Body1>Could not open this document: {state.message}</Body1>
      </div>
    )
  }
  return (
    <div style={{ height: '100vh', boxSizing: 'border-box', padding: 16 }}>
      {/* `?rotate=` carries the modal's turn into this tab (2026-10-01); the
          rotate buttons work here as they do in the modal. */}
      <PdfPane
        upload={state.upload}
        pageStart={state.pageStart}
        pageEnd={state.pageEnd}
        initialRotation={rotationFromSearch(window.location.search)}
      />
    </div>
  )
}
